import React from "react";
import {
  Box,
  Card,
  CardContent,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { getIsMobile } from "../../../utils/helpers";

interface FormSectionProps {
  title: string | React.ReactNode;
  children: React.ReactNode;
  expanded?: boolean;
  onToggle?: () => void;
  completed?: boolean;
  collapsible?: boolean;
  error?: string;
}

/**
 * FormSection Component
 * 
 * This component provides a consistent wrapper for form sections.
 * It includes a title, optional subtitle, and collapsible content.
 */
const FormSection: React.FC<FormSectionProps> = ({
  title,
  children,
  onToggle,
  completed = false,
  collapsible = true,
  error,
}) => {
  const handleToggle = () => {
    if (collapsible && onToggle) {
      onToggle();
    }
  };

  const isMobile = getIsMobile();

  return (
    <Card variant="outlined" sx={{ mb: 3, p: { xs: 0, md: 3 } }}>
      {!isMobile && <Box
        sx={{
          pb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: collapsible ? "pointer" : "default",
          bgcolor: completed ? "success.light" : "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
        onClick={handleToggle}
      >
        <Stack direction="column" spacing={1}>
          {completed && (
            <CheckCircleIcon color="success" fontSize="small" />
          )}
          <Typography variant="h5" component="h2">
            {title}
          </Typography>
        </Stack>
      </Box>}

      <CardContent>
        {error && (
          <>
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </>
        )}
        {children}
      </CardContent>
    </Card>
  );
};

export default FormSection;
